import { useEffect, useMemo, useRef, useState } from 'react';

import { ChakraFlex } from '@equip.health/ui';

import TreatmentEpisodeAccordion from '~/components/maud/patients/treatmentEpisode/Accordion';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useModeContext, UserMode } from '~/lib/contexts/ModeContext';

import GlobalErrorToast from './GlobalErrorToast';
import ToastAccordion from './ToastAccordion';
import AddEditProfileAccordion from './user-profile/AddEditProfileAccordion';
import EditAboutMeAccordion from './user-profile/EditAboutMeAccordion';
import EditPermissionAccordion from './user-profile/EditPermissionAccordion';
import OutOfOfficeAccordion from './user-profile/OutOfOfficeAccordion';
import AddGroupClassAccordion from '../maud/calendar/group-class/AddGroupClassAccordion';
import CreateInquiryDrawer from '../maud/inquiries/create';
import { InquiryDetailDrawer } from '../maud/inquiries/InquiryDetail';
import BillingPeriodAccordion from '../maud/patients/profile/billingPeriods/BillingPeriodAccordion';
import AddEditSurveyRespondentAccordion from '../maud/patients/profile/surveys/AddEditSurveyRespondentAccordion';
import SurveyRespondentModal from '../maud/patients/profile/surveys/SurveyRespondentModal';
import SurveyAccordion from '../maud/survey';
import NewTaskAccordion from '../maud/tasks/NewTaskAccordion';
import { NoteAccordionComponentChooser } from '~/components/common/NoteAccordionComponentChooser';
import EditGroupClassAccordion from '../maud/calendar/group-class/EditGroupClassAccordion';
import ProviderAssignmentModal from '../maud/providerAssignment';
import AddReassignmentModal from '../maud/reassignment/AddReassignmentModal';

const ComponentPortal = () => {
  const componentPortalContext = useComponentPortalContext();

  const { currentPatientId } = useGlobalContext();
  const { mode } = useModeContext();

  const accordionParentRef = useRef<HTMLDivElement>(null);
  const expandedAccordionWidthTimerRef = useRef(null);
  const resizeObserver = useRef<ResizeObserver>();

  const [expandedAccordionWidth, setExpandedAccordionWidth] =
    useState<number>(0);

  const {
    addReassignmentModal,
    addEditSurveyRespondentAccordion,
    addGroupClassAccordion,
    addTaskAccordion,
    createInquiryDrawer,
    editGroupClassAccordion,
    editPermissionsAccordion,
    editProfileAboutMeAccordion,
    editProfileAccordion,
    errorToast,
    inquiryDetailDrawer,
    noteAccordion,
    outOfOfficeAccordion,
    providerAssignmentModal,
    setComponentVisibility,
    surveyAccordion,
    toastAccordion,
    billingPeriodAccordion,
    treatmentEpisodeAccordion,
  } = componentPortalContext;

  const isPatientInTreatment =
    addEditSurveyRespondentAccordion?.data?.hasEquipAccount;

  const currentlyExpandedAccordion = useMemo<string>(
    () =>
      Object.keys(componentPortalContext).find(
        (key) =>
          componentPortalContext[key].isExpanded &&
          componentPortalContext[key].isVisible,
      ),
    [
      ...Object.keys(componentPortalContext).map(
        (key) => componentPortalContext[key].isVisible,
      ),
      ...Object.keys(componentPortalContext).map(
        (key) => componentPortalContext[key].isExpanded,
      ),
    ],
  );

  const getExpandedAccordionWidth = (): number => {
    if (!accordionParentRef.current) {
      return 0;
    }

    const expandedAccordions =
      accordionParentRef.current.getElementsByClassName('isExpanded');

    // We make sure only one accordion get expanded at a time, in ComponentPortalContext
    return (expandedAccordions ?? []).length > 0
      ? expandedAccordions[0].clientWidth
      : 0;
  };

  useEffect(() => {
    if (currentlyExpandedAccordion && accordionParentRef.current) {
      setExpandedAccordionWidth(getExpandedAccordionWidth());

      if (expandedAccordionWidthTimerRef.current)
        clearTimeout(expandedAccordionWidthTimerRef.current);

      // Accordion will animate and expand to its full width.
      // To get final width, we have a timeout.
      expandedAccordionWidthTimerRef.current = setTimeout(() => {
        setExpandedAccordionWidth(getExpandedAccordionWidth());
      }, 400);

      // Few Accordion width depends on viewport width. To handle that, we have a resizeObserver
      if (!resizeObserver.current) {
        resizeObserver.current = new ResizeObserver(() =>
          setExpandedAccordionWidth(getExpandedAccordionWidth()),
        );
        resizeObserver.current.observe(accordionParentRef.current);
      }
    } else {
      if (resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = null;
      }
      setExpandedAccordionWidth(0);
    }

    return () => {
      if (expandedAccordionWidthTimerRef.current)
        clearTimeout(expandedAccordionWidthTimerRef.current);
    };
  }, [currentlyExpandedAccordion]);

  const dismissAccordion = (component: PortalComponent, data = null): void => {
    setComponentVisibility({
      component,
      data,
      hasUnsavedChanges: false,
      isExpanded: false,
      isFullScreen: false,
      isVisible: false,
    });
  };

  const wrapperZIndex = useMemo(() => {
    if (errorToast.isVisible) return 1401;
    if (currentlyExpandedAccordion) return 100;
    return 1;
  }, [currentlyExpandedAccordion, errorToast.isVisible]);

  return (
    <ChakraFlex
      alignItems="end"
      bottom={0}
      flexDirection="row-reverse"
      flexWrap="wrap-reverse"
      justifyContent="end"
      left={0}
      marginRight={`${80 + expandedAccordionWidth}px`}
      position="absolute"
      ref={accordionParentRef}
      right={0}
      zIndex={wrapperZIndex}
    >
      {mode === UserMode.MAUD && (
        <>
          {billingPeriodAccordion.isVisible && (
            <BillingPeriodAccordion
              currentBillingPeriod={billingPeriodAccordion.data}
              dismissBillingPeriod={() => {
                dismissAccordion(
                  PortalComponent.BILLING_PERIOD_ACCORDION,
                  null,
                );
              }}
              isExpanded={billingPeriodAccordion.isExpanded}
              setIsExpanded={(isExpanded: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.BILLING_PERIOD_ACCORDION,
                  isExpanded,
                })
              }
            />
          )}
          {addGroupClassAccordion.isVisible && (
            <AddGroupClassAccordion
              data={addGroupClassAccordion.data}
              dismissAddGroupClass={() => {
                dismissAccordion(PortalComponent.ADD_GROUP_CLASS_ACCORDION);
              }}
              isExpanded={addGroupClassAccordion.isExpanded}
              setIsExpanded={(isExpanded: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.ADD_GROUP_CLASS_ACCORDION,
                  isExpanded,
                })
              }
            />
          )}
          {editGroupClassAccordion.isVisible && (
            <EditGroupClassAccordion
              data={editGroupClassAccordion.data}
              dismissEditGroupClass={() => {
                dismissAccordion(
                  PortalComponent.EDIT_GROUP_CLASS_ACCORDION,
                  null,
                );
              }}
              isExpanded={editGroupClassAccordion.isExpanded}
              setIsExpanded={(isExpanded: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.EDIT_GROUP_CLASS_ACCORDION,
                  isExpanded,
                })
              }
            />
          )}
          {addTaskAccordion.isVisible && (
            <NewTaskAccordion
              triggerSource={addTaskAccordion.data?.triggerSource}
              dismissAddTask={() =>
                dismissAccordion(PortalComponent.ADD_TASK_ACCORDION)
              }
              isExpanded={addTaskAccordion.isExpanded}
              setIsExpanded={(isExpanded: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.ADD_TASK_ACCORDION,
                  isExpanded,
                })
              }
              showLinkedPatient={addTaskAccordion.showLinkedPatient}
            />
          )}
          {noteAccordion.isVisible && (
            <NoteAccordionComponentChooser
              currentPatientId={currentPatientId}
              data={noteAccordion.data}
              dismissAccordion={dismissAccordion}
              isExpanded={noteAccordion.isExpanded}
              setComponentVisibility={setComponentVisibility}
            />
          )}
          {surveyAccordion.isVisible && (
            <SurveyAccordion
              data={surveyAccordion.data}
              isExpanded={surveyAccordion.isExpanded}
              isFullScreen={surveyAccordion.isFullScreen}
              onDismiss={() =>
                dismissAccordion(PortalComponent.SURVEY_ACCORDION)
              }
              setIsExpanded={(isExpanded: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.SURVEY_ACCORDION,
                  isExpanded,
                })
              }
              setIsFullScreen={(isFullScreenValue: boolean) =>
                setComponentVisibility({
                  component: PortalComponent.SURVEY_ACCORDION,
                  isFullScreen: isFullScreenValue,
                })
              }
            />
          )}
          {toastAccordion.isVisible && (
            <ToastAccordion
              data={toastAccordion.data}
              isExpanded={toastAccordion.isExpanded}
              onDismiss={() =>
                dismissAccordion(PortalComponent.TOAST_ACCORDION)
              }
              setIsExpanded={(isExpanded: boolean) => {
                setComponentVisibility({
                  component: PortalComponent.TOAST_ACCORDION,
                  isExpanded,
                });
              }}
            />
          )}
          {addEditSurveyRespondentAccordion.isVisible &&
            (isPatientInTreatment ? (
              <SurveyRespondentModal
                data={addEditSurveyRespondentAccordion.data}
                isOpen={addEditSurveyRespondentAccordion.isExpanded}
                onDismiss={() =>
                  dismissAccordion(
                    PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
                  )
                }
              />
            ) : (
              <AddEditSurveyRespondentAccordion
                data={addEditSurveyRespondentAccordion.data}
                isExpanded={addEditSurveyRespondentAccordion.isExpanded}
                onDismiss={() =>
                  dismissAccordion(
                    PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
                  )
                }
                setIsExpanded={(isExpanded: boolean) => {
                  setComponentVisibility({
                    component:
                      PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
                    isExpanded,
                  });
                }}
              />
            ))}

          <GlobalErrorToast
            show={errorToast.isVisible}
            statusCode={errorToast.data?.statusCode}
          />
        </>
      )}
      {outOfOfficeAccordion.isVisible && (
        <OutOfOfficeAccordion
          isExpanded={outOfOfficeAccordion.isExpanded}
          onDismissOOOAccordion={() =>
            dismissAccordion(PortalComponent.OUT_OF_OFFICE_ACCORDION)
          }
          oooData={outOfOfficeAccordion.data}
          setIsExpanded={(isExpanded: boolean) =>
            setComponentVisibility({
              component: PortalComponent.OUT_OF_OFFICE_ACCORDION,
              isExpanded,
            })
          }
        />
      )}
      {editProfileAboutMeAccordion.isVisible && (
        <EditAboutMeAccordion
          aboutMeData={editProfileAboutMeAccordion.data}
          isExpanded={editProfileAboutMeAccordion.isExpanded}
          onDismissEditAboutMeAccordion={() =>
            dismissAccordion(PortalComponent.EDIT_ABOUTME_ACCORDION)
          }
          setIsExpanded={(isExpanded: boolean) =>
            setComponentVisibility({
              component: PortalComponent.EDIT_ABOUTME_ACCORDION,
              isExpanded,
            })
          }
        />
      )}
      {editPermissionsAccordion.isVisible && (
        <EditPermissionAccordion
          isExpanded={editPermissionsAccordion.isExpanded}
          onDismissEditPermissionAccordion={() =>
            dismissAccordion(PortalComponent.EDIT_PERMISSIONS_ACCORDION)
          }
          permissionData={editPermissionsAccordion.data}
          setIsExpanded={(isExpanded: boolean) =>
            setComponentVisibility({
              component: PortalComponent.EDIT_PERMISSIONS_ACCORDION,
              isExpanded,
            })
          }
        />
      )}

      {editProfileAccordion.isVisible && (
        <AddEditProfileAccordion
          isExpanded={editProfileAccordion.isExpanded}
          onDismissEditProfileAccordion={() =>
            dismissAccordion(PortalComponent.ADD_EDIT_PROFILE_ACCORDION)
          }
          profileData={editProfileAccordion.data}
          setIsExpanded={(isExpanded: boolean) =>
            setComponentVisibility({
              component: PortalComponent.ADD_EDIT_PROFILE_ACCORDION,
              isExpanded,
            })
          }
        />
      )}

      {treatmentEpisodeAccordion.isVisible && (
        <TreatmentEpisodeAccordion
          dismissMenu={() =>
            dismissAccordion(PortalComponent.TREATMENT_EPISODE_ACCORDION)
          }
          isExpanded={treatmentEpisodeAccordion.isExpanded}
          setIsExpanded={(isExpanded: boolean) =>
            setComponentVisibility({
              component: PortalComponent.TREATMENT_EPISODE_ACCORDION,
              isExpanded,
            })
          }
          treatmentEpisodeData={treatmentEpisodeAccordion.data}
        />
      )}

      {createInquiryDrawer.isVisible && <CreateInquiryDrawer />}

      {inquiryDetailDrawer.isVisible && <InquiryDetailDrawer />}

      {providerAssignmentModal.isVisible && <ProviderAssignmentModal />}

      {addReassignmentModal.isVisible && <AddReassignmentModal />}
    </ChakraFlex>
  );
};

export default ComponentPortal;
